import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BasicDialogService,
  isDisabled$,
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  ChartedTreatment,
  MockFeeSchedule,
  TreatmentConfiguration,
} from '@principle-theorem/principle-core';
import {
  ChartableSurface,
  type IChartedTreatment,
  type IPricedServiceCodeEntry,
  type ITreatmentConfiguration,
  type ITreatmentPackage,
  type ITreatmentPackagePriceOverride,
  ServiceCodeGroupType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { MockWithRef } from '@principle-theorem/testing';
import { type Observable, ReplaySubject } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

export type TreatmentPackageForm = Omit<ITreatmentPackage, 'uid'>;

@Component({
  selector: 'pr-edit-treatment-package',
  templateUrl: './edit-treatment-package.component.html',
  styleUrls: ['./edit-treatment-package.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditTreatmentPackageComponent {
  private _dialog = inject(BasicDialogService);
  private _snackBar = inject(MatSnackBar);

  treatmentPackage$ = new ReplaySubject<ITreatmentPackage>(1);
  treatmentConfig$ = new ReplaySubject<WithRef<ITreatmentConfiguration>>(1);
  treatment$: Observable<IChartedTreatment>;
  serviceCodes$: Observable<IPricedServiceCodeEntry[]>;
  form = new TypedFormGroup<TreatmentPackageForm>({
    name: new TypedFormControl(''),
    description: new TypedFormControl(''),
    isDefault: new TypedFormControl(false),
    basePriceOverride: new TypedFormControl<number | undefined>(undefined),
    priceOverrides: new TypedFormArray<ITreatmentPackagePriceOverride>([]),
  });
  totalPrice$ = new ReplaySubject<number>(1);
  isDisabled$ = isDisabled$(this.form);
  @Output() updatePackage = new EventEmitter<TreatmentPackageForm>();
  @Output() deletePackage = new EventEmitter<void>();

  @Input()
  set treatmentPackage(treatmentPackage: ITreatmentPackage) {
    if (treatmentPackage) {
      this.treatmentPackage$.next(treatmentPackage);
    }
  }

  @Input()
  set treatmentConfig(treatmentConfig: WithRef<ITreatmentConfiguration>) {
    if (treatmentConfig) {
      this.treatmentConfig$.next(treatmentConfig);
    }
  }

  get priceOverrides(): TypedFormArray<ITreatmentPackagePriceOverride> {
    return this.form.controls
      .priceOverrides as TypedFormArray<ITreatmentPackagePriceOverride>;
  }

  constructor() {
    this.treatment$ = this.treatmentConfig$.pipe(
      map((treatmentConfig) =>
        ChartedTreatment.fromConfig(
          treatmentConfig,
          MockWithRef(MockFeeSchedule()),
          {
            scope: ChartableSurface.Unscoped,
          }
        )
      )
    );

    this.serviceCodes$ = this.treatmentConfig$.pipe(
      map((treatmentConfig) =>
        TreatmentConfiguration.getCombinedServiceCodes(treatmentConfig, [
          ServiceCodeGroupType.Required,
          ServiceCodeGroupType.Exclusive,
          ServiceCodeGroupType.Smart,
        ])
      )
    );

    this.treatmentPackage$
      .pipe(takeUntilDestroyed())
      .subscribe((treatmentPackage) => {
        this.form.patchValue(treatmentPackage);
        this.updateServiceItems(treatmentPackage.priceOverrides, false);
      });

    this.form.valueChanges
      .pipe(
        startWith(this.form.getRawValue()),
        switchMap(async (form) => {
          const priceOverrides = form.priceOverrides;
          const serviceCodes = await asyncForEach(
            priceOverrides,
            async (priceOverride) => ({
              ...priceOverride,
              quantity: await this.resolveQuantity(priceOverride),
            })
          );
          return (
            serviceCodes.reduce(
              (total, serviceCode) =>
                total + serviceCode.quantity * (serviceCode.price ?? 0),
              0
            ) + (form.basePriceOverride ?? 0)
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((total) => this.totalPrice$.next(total));
  }

  save(): void {
    if (!this.form.valid) {
      return;
    }
    const data = this.form.getRawValue();
    this.updatePackage.emit({
      name: data.name,
      description: data.description,
      isDefault: data.isDefault ?? false,
      basePriceOverride: data.basePriceOverride,
      priceOverrides: this.priceOverrides.getRawValue(),
    });
    this.form.markAsPristine();
    this.form.updateValueAndValidity();
    this._snackBar.open('Package updated');
  }

  async delete(): Promise<void> {
    const confirmed = await this._dialog.confirm({
      prompt: 'Are you sure you want to delete this package?',
      title: 'Delete',
      submitLabel: 'Yes, Delete',
      submitColor: 'warn',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    this.deletePackage.emit();
  }

  updateServiceItems(
    priceOverrides: ITreatmentPackagePriceOverride[],
    markDirty = true
  ): void {
    this.priceOverrides.clear();
    priceOverrides.forEach((priceOverride) =>
      this.priceOverrides.push(
        new TypedFormGroup<ITreatmentPackagePriceOverride>({
          code: new TypedFormControl(priceOverride.code),
          type: new TypedFormControl(priceOverride.type),
          price: new TypedFormControl(
            priceOverride.price ?? undefined,
            Validators.required
          ),
        })
      )
    );

    if (markDirty) {
      this.form.markAsDirty();
      this.form.updateValueAndValidity();
    }
  }

  updateBasePriceOverride(basePriceOverride: number): void {
    this.form.controls.basePriceOverride.setValue(basePriceOverride);
    this.form.markAsDirty();
  }

  async resolveQuantity(
    scopedCode: ITreatmentPackagePriceOverride
  ): Promise<number> {
    const foundCode = await snapshot(
      this.serviceCodes$.pipe(
        map((serviceCodes) =>
          serviceCodes.find(
            (serviceCode) =>
              serviceCode.code === scopedCode.code &&
              serviceCode.type === scopedCode.type
          )
        )
      )
    );
    return foundCode?.quantity ?? 0;
  }
}
